import { ChatThread } from "@/components/conversations";
import {
  MessageInput,
  type ResolvedOpsChannel,
} from "@/components/conversations/message-input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useSignedMedia } from "@/lib/hooks/use-signed-media";
import { getInitials } from "@/lib/utils";
import {
  InternalOpsChannel,
  RecipientSuggestion,
  useCreateInternalOpsChannel,
  useFindInternalOpsChannelByRecipients,
  useInternalOpsChannel,
  useRecipientSuggestions,
} from "@/lib/api/internal-ops-messaging";
import { PostHogEvents, useTrackEvent } from "@/lib/posthog-events";
import { formatRoleLabel } from "@/lib/utils/role-label";
import { cn } from "@/lib/utils";
import { XMarkIcon } from "@heroicons/react/24/outline";
import useSendbirdStateContext from "@sendbird/uikit-react/useSendbirdStateContext";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

interface NewChatViewProps {
  onCancel: () => void;
  onChannelReady: (payload: {
    sdkUrl: string;
    internalChannelId: string;
  }) => void;
}

export function NewChatView({ onCancel, onChannelReady }: NewChatViewProps) {
  const { track } = useTrackEvent();
  const [recipients, setRecipients] = useState<RecipientSuggestion[]>([]);
  const [query, setQuery] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(true);
  const inputRef = useRef<HTMLInputElement>(null);
  const comboboxRef = useRef<HTMLDivElement>(null);
  const viewTracked = useRef(false);

  useEffect(() => {
    const handleMouseDown = (event: MouseEvent) => {
      if (!comboboxRef.current) return;
      if (!comboboxRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, []);

  // For a single cohort recipient, the cohort already maps to a pre-provisioned
  // internal channel. For everything else, dedupe via the find endpoint.
  const isSingleCohort =
    recipients.length === 1 && recipients[0].type === "cohort";
  const cohortChannelId = isSingleCohort ? recipients[0].id : null;
  const userRecipientIds = useMemo(
    () =>
      recipients
        .filter(
          (r): r is Extract<RecipientSuggestion, { type: "user" }> =>
            r.type === "user",
        )
        .map((r) => r.id),
    [recipients],
  );

  const { data: cohortChannel } = useInternalOpsChannel(cohortChannelId);
  const { data: matchedChannel } =
    useFindInternalOpsChannelByRecipients(userRecipientIds);
  const { data: suggestions = [], isLoading: suggestionsLoading } =
    useRecipientSuggestions(query);

  const existingChannel: InternalOpsChannel | null = isSingleCohort
    ? (cohortChannel ?? null)
    : (matchedChannel ?? null);
  const lastNotifiedChannelIdRef = useRef<string | null>(null);

  const sbStore = useSendbirdStateContext();
  const sdk = sbStore?.stores?.sdkStore?.sdk;
  const createChannel = useCreateInternalOpsChannel();
  const lastResolvedRef = useRef<ResolvedOpsChannel | null>(null);
  const containsCohort = recipients.some((r) => r.type === "cohort");

  const ensureChannel = useCallback(async (): Promise<ResolvedOpsChannel> => {
    if (!sdk) throw new Error("Sendbird SDK not ready");
    if (recipients.length === 0) throw new Error("No recipients selected");
    const userIds = recipients
      .filter(
        (r): r is Extract<RecipientSuggestion, { type: "user" }> =>
          r.type === "user",
      )
      .map((r) => r.id);
    const channel = await createChannel.mutateAsync({ recipientIds: userIds });
    const groupChannel = await sdk.groupChannel.getChannel(channel.sdk_url);
    const resolved: ResolvedOpsChannel = {
      groupChannel,
      internalChannelId: channel.id,
    };
    lastResolvedRef.current = resolved;
    return resolved;
  }, [sdk, recipients, createChannel]);

  const handleNewChatSent = useCallback(() => {
    const resolved = lastResolvedRef.current;
    if (!resolved) return;
    const sdkUrl = resolved.groupChannel.url;
    // OPS_CHAT_MESSAGE_SEND for this first message is tracked by MessageInput
    // (with is_first_message=true), so we only emit the new-chat-specific event
    // here to avoid double-firing the send.
    track(PostHogEvents.OPS_CHAT_NEW_CHAT_SEND, {
      recipient_count: recipients.length,
      is_cohort: containsCohort,
      merged_with_existing_thread: false,
      thread_id: sdkUrl,
    });
    onChannelReady({
      sdkUrl,
      internalChannelId: resolved.internalChannelId,
    });
    lastResolvedRef.current = null;
  }, [track, recipients.length, containsCohort, onChannelReady]);

  const filteredSuggestions = useMemo(
    () =>
      suggestions.filter(
        (s) => !recipients.some((r) => r.id === s.id && r.type === s.type),
      ),
    [suggestions, recipients],
  );

  useEffect(() => {
    if (viewTracked.current) return;
    if (suggestionsLoading) return;
    viewTracked.current = true;
    track(PostHogEvents.OPS_CHAT_NEW_CHAT_DROPDOWN_VIEW, {
      default_count: suggestions.length,
      query_chars: 0,
    });
    inputRef.current?.focus();
  }, [suggestionsLoading, suggestions.length, track]);

  useEffect(() => {
    if (!query || suggestionsLoading) return;
    track(PostHogEvents.OPS_CHAT_NEW_CHAT_DROPDOWN_FILTER, {
      query_chars: query.length,
      match_count: suggestions.length,
    });
  }, [query, suggestionsLoading, suggestions.length, track]);

  const handleAddRecipient = (
    s: RecipientSuggestion,
    method: "enter" | "click",
  ) => {
    if (recipients.some((r) => r.id === s.id && r.type === s.type)) return;
    track(PostHogEvents.OPS_CHAT_NEW_CHAT_RECIPIENT_ADD, {
      recipient_id: s.id,
      recipient_type: s.type === "user" ? "user" : "cohort_auto",
      method,
    });
    setRecipients((prev) => [...prev, s]);
    setQuery("");
    inputRef.current?.focus();
  };

  const handleRemoveRecipient = (r: RecipientSuggestion) => {
    track(PostHogEvents.OPS_CHAT_NEW_CHAT_RECIPIENT_REMOVE, {
      recipient_id: r.id,
    });
    setRecipients((prev) =>
      prev.filter((x) => !(x.id === r.id && x.type === r.type)),
    );
  };

  const handleCancel = () => {
    track(PostHogEvents.OPS_CHAT_NEW_CHAT_DISMISS, {
      recipient_count: recipients.length,
      had_query: query.length > 0,
    });
    onCancel();
  };

  const handleQueryChange = (value: string) => {
    setQuery(value);
    setIsDropdownOpen(true);
  };

  const handleInputFocus = () => {
    setIsDropdownOpen(true);
  };

  return (
    <div className="flex flex-1 flex-col bg-white">
      <div ref={comboboxRef} className="relative z-20">
        <RecipientBar
          recipients={recipients}
          query={query}
          onQueryChange={handleQueryChange}
          topSuggestion={filteredSuggestions[0] ?? null}
          onAddRecipient={handleAddRecipient}
          onRemoveRecipient={handleRemoveRecipient}
          onCancel={handleCancel}
          inputRef={inputRef}
          onInputFocus={handleInputFocus}
        />

        {isDropdownOpen && (
          <div className="absolute inset-x-0 top-full p-4">
            <SuggestionDropdown
              suggestions={filteredSuggestions}
              loading={suggestionsLoading}
              query={query}
              onSelect={(s) => handleAddRecipient(s, "click")}
            />
          </div>
        )}
      </div>

      {existingChannel ? (
        <ChatThread
          key={existingChannel.sdk_url}
          channelUrl={existingChannel.sdk_url}
          canCreateMessages
          opsChannel={existingChannel}
          channelHeader={null}
          onAfterSend={() => {
            if (lastNotifiedChannelIdRef.current === existingChannel.id) return;
            lastNotifiedChannelIdRef.current = existingChannel.id;
            onChannelReady({
              sdkUrl: existingChannel.sdk_url,
              internalChannelId: existingChannel.id,
            });
          }}
        />
      ) : (
        <>
          <div className="flex-1" />
          {recipients.length > 0 && (
            <MessageInput
              shouldShowAiConversation={false}
              opsContext={{
                internalChannelId: null,
                ensureChannel,
                onAfterSend: handleNewChatSent,
              }}
            />
          )}
        </>
      )}
    </div>
  );
}

interface RecipientBarProps {
  recipients: RecipientSuggestion[];
  query: string;
  onQueryChange: (value: string) => void;
  topSuggestion: RecipientSuggestion | null;
  onAddRecipient: (
    suggestion: RecipientSuggestion,
    method: "enter" | "click",
  ) => void;
  onRemoveRecipient: (recipient: RecipientSuggestion) => void;
  onCancel: () => void;
  inputRef: React.RefObject<HTMLInputElement>;
  onInputFocus?: () => void;
}

function RecipientBar({
  recipients,
  query,
  onQueryChange,
  topSuggestion,
  onAddRecipient,
  onRemoveRecipient,
  onCancel,
  inputRef,
  onInputFocus,
}: RecipientBarProps) {
  return (
    <div className="flex min-h-16 items-center gap-2 border-b border-zinc-200 px-4 py-2">
      <span className="text-xs leading-4 text-zinc-400">To:</span>
      <div className="flex flex-1 flex-wrap items-center gap-1">
        {recipients.map((r) => (
          <RecipientPill
            key={`${r.type}-${r.id}`}
            recipient={r}
            onRemove={() => onRemoveRecipient(r)}
          />
        ))}
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          onFocus={onInputFocus}
          onKeyDown={(e) => {
            if (e.key === "Enter" && topSuggestion) {
              e.preventDefault();
              onAddRecipient(topSuggestion, "enter");
            } else if (
              e.key === "Backspace" &&
              !query &&
              recipients.length > 0
            ) {
              onRemoveRecipient(recipients[recipients.length - 1]);
            } else if (e.key === "Escape") {
              onCancel();
            }
          }}
          className="min-w-[120px] flex-1 border-0 bg-transparent text-sm text-zinc-900 outline-none placeholder:text-zinc-400"
        />
      </div>
      <button
        type="button"
        onClick={onCancel}
        className="text-zinc-400 hover:text-zinc-600"
        aria-label="Cancel new chat"
      >
        <XMarkIcon className="size-5" />
      </button>
    </div>
  );
}

function SuggestionDropdown({
  suggestions,
  loading,
  query,
  onSelect,
}: {
  suggestions: RecipientSuggestion[];
  loading: boolean;
  query: string;
  onSelect: (suggestion: RecipientSuggestion) => void;
}) {
  if (loading && suggestions.length === 0) {
    return (
      <div className="rounded-lg border border-zinc-200 bg-white px-4 py-3 text-sm text-zinc-400 shadow-[0_2px_8px_0_rgba(0,0,0,0.05)]">
        Loading…
      </div>
    );
  }

  if (suggestions.length === 0) {
    return query ? (
      <div className="rounded-lg border border-zinc-200 bg-white px-4 py-3 text-sm text-zinc-400 shadow-[0_2px_8px_0_rgba(0,0,0,0.05)]">
        No matches found
      </div>
    ) : null;
  }

  return (
    <div className="overflow-hidden rounded-lg border border-zinc-200 bg-white shadow-[0_2px_8px_0_rgba(0,0,0,0.05)]">
      {suggestions.map((s) => (
        <SuggestionRow
          key={`${s.type}-${s.id}`}
          suggestion={s}
          onSelect={() => onSelect(s)}
        />
      ))}
    </div>
  );
}

function SuggestionRow({
  suggestion,
  onSelect,
}: {
  suggestion: RecipientSuggestion;
  onSelect: () => void;
}) {
  const { url: avatarUrl } = useSignedMedia(
    suggestion.type === "user" ? (suggestion.avatar_url ?? "") : "",
  );
  const subtitle =
    suggestion.type === "user"
      ? (formatRoleLabel(suggestion.role) ?? "")
      : "Cohort";

  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onSelect}
      className={cn(
        "flex w-full items-center gap-2 bg-white px-4 py-1 text-left transition-colors hover:bg-zinc-50",
      )}
    >
      <Avatar className="size-10 shrink-0">
        <AvatarImage src={avatarUrl} alt={suggestion.name} />
        <AvatarFallback className="bg-zinc-200 text-sm text-zinc-500">
          {getInitials(suggestion.name)}
        </AvatarFallback>
      </Avatar>
      <div className="flex flex-col items-start p-2">
        <span className="text-base font-bold leading-6 text-black">
          {suggestion.name}
        </span>
        <span className="pt-1 text-xs leading-4 text-zinc-400">{subtitle}</span>
      </div>
    </button>
  );
}

function RecipientPill({
  recipient,
  onRemove,
}: {
  recipient: RecipientSuggestion;
  onRemove: () => void;
}) {
  const { url: avatarUrl } = useSignedMedia(
    recipient.type === "user" ? (recipient.avatar_url ?? "") : "",
  );

  return (
    <span className="flex h-10 items-center gap-2 rounded-lg border border-zinc-200 bg-white px-2">
      <Avatar className="size-7 shrink-0">
        <AvatarImage src={avatarUrl} alt={recipient.name} />
        <AvatarFallback className="bg-zinc-200 text-xs text-zinc-500">
          {getInitials(recipient.name)}
        </AvatarFallback>
      </Avatar>
      <span className="text-base font-bold leading-6 text-black">
        {recipient.name}
      </span>
      <button
        type="button"
        onClick={onRemove}
        className="text-zinc-500 hover:text-zinc-700"
        aria-label={`Remove ${recipient.name}`}
      >
        <XMarkIcon className="size-5" />
      </button>
    </span>
  );
}
